import { Box, Typography } from '@material-ui/core';
import { WbCardContent } from '../../WbCard';
import { WbNoData } from '../../WbNoData';
import { useGeneralPurposePickerContext } from '../GeneralPurposePickerContext';
import {
  GenericEntityType,
  Provider,
  TemplateEntity,
  TextFieldValue,
} from '../types';
import { ProviderFilters } from './CommonFilter';
import { EntityKindSelector } from './EntityKindSelector';
import { SelectableEntityTable } from './SelectableEntityTable';
import { selectTemplateEntity } from './utils';
import { WbAlert } from '../../WbAlert';
import { useMemo } from 'react';
import { useLoaderNunjucks } from '../../../hooks';

interface SingleSelectionProps {
  availableKinds: TemplateEntity[];
  providerMapper: Map<string, Provider<GenericEntityType[], ProviderFilters>>;
  parentTemplateRef?: string;
  selectedEntityProvider?:
    | Provider<GenericEntityType[], ProviderFilters>
    | undefined;
}

export const SingleSelectionTable = ({
  selectedEntityProvider,
  entities,
  setEntities,
}: {
  selectedEntityProvider: Provider<GenericEntityType[], ProviderFilters>;

  entities: TextFieldValue[];
  setEntities: (values: TextFieldValue[]) => void;
}) => {
  const context = selectedEntityProvider?.useContext();

  return (
    <SelectableEntityTable
      loading={context?.entitiesState.loading ?? false}
      data={(context?.entitiesState.value as GenericEntityType[]) ?? []}
      entities={entities}
      adapter={entity => selectedEntityProvider?.adapt(entity)}
      onChangeHandler={setEntities}
      filtersSection={selectedEntityProvider?.filtersSection!}
      pagination={context?.pagination!}
      setPagination={context?.setPagination!}
      count={context?.count ?? 0}
      nextCursor={context?.nextCursor}
      prevCursor={context?.prevCursor}
      columns={context?.columns ?? []}
      templateEntity={context?.templateKind!}
    />
  );
};

export function SingleSelection(props: SingleSelectionProps) {
  const { availableKinds, parentTemplateRef } = props;
  const {
    selectedKind,
    selectedValues,
    setSelectedValues,
    setOpenDialog,
    selectedEntityProvider,
    errorMessage,
  } = useGeneralPurposePickerContext();

  const selectedTemplate = useMemo(
    () => selectTemplateEntity(availableKinds, selectedKind),
    [availableKinds, selectedKind],
  );

  // Preload nunjucks template before rendering the table, setEntities needed parsed display field
  const displayField = selectedTemplate?.displayField
    ? [selectedTemplate?.displayField]
    : undefined;
  useLoaderNunjucks(displayField);

  const renderedComponent = useMemo(
    () =>
      selectedEntityProvider?.renderContextProvider(
        selectedTemplate,
        <SingleSelectionTable
          selectedEntityProvider={selectedEntityProvider}
          entities={selectedValues?.tableValues ?? []}
          setEntities={e => {
            setSelectedValues({ tableValues: e });
            setOpenDialog(false);
          }}
        />,
        parentTemplateRef,
      ),
    [
      selectedEntityProvider,
      selectedTemplate,
      selectedValues,
      parentTemplateRef,
      setSelectedValues,
      setOpenDialog,
    ],
  );

  return (
    <WbCardContent
      style={{
        height: '64vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {errorMessage && <WbAlert closable message={errorMessage} />}
      <Box display="flex" justifyContent="space-between" paddingY="8px">
        <Typography variant="body1">
          Select an entity from the list below
        </Typography>
        {availableKinds.length > 1 && (
          <EntityKindSelector availableKinds={availableKinds} />
        )}
      </Box>

      {renderedComponent ?? <WbNoData />}
    </WbCardContent>
  );
}
